import styles from './newsEditor.module.css';
import React, { useEffect, useState } from "react";
import { usePermissionGuard, useCheckPermission } from '../../adminPanelTools';
import CategoryDropdown from './CategoryDropdown';
import ImageRadioButtons from './ImageRadioButtons';
import getDefaultImageForCategory from '../../../../config/defaultImages';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertFromRaw, convertToRaw } from 'draft-js';
import { convertToHTML, convertFromHTML } from 'draft-convert';
import DOMPurify from 'dompurify';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { generateOption1HTML } from './layouts/layoutOption1';
import { generateOption2HTML } from './layouts/layoutOption2';
import { generateOption3HTML } from './layouts/layoutOption3';
import StyleOneContent from './StyleOneContent';
import StyleTwoContent from './StyleTwoContent';
import StyleThreeContent from './StyleThreeContent';
import { showErrorToast, showInfoToast, showWarningToast } from '../../../../code/ToastHelper';
import { Icons } from '../../../../code/IconHandler';
// import NewsContentEditor from './NewsContentEditor';



function NewsEditor({ initialData = null, onClose }) {
    const checking = usePermissionGuard('handle_news');
    const { hasPermission: canSetAnyAuthor, checkingAnyAuthorPermission } = useCheckPermission('set_any_author');
    const [currentUser, setCurrentUser] = useState(null);
    const [isMounted, setIsMounted] = useState(false);
    const [autorFromInitialData, setAutorFromInitialData] = useState(false);
    const [selectedRadio, setSelectedRadio] = React.useState('option1');
    const [categories, setCategories] = useState([]);
    const [highlightEditorState, setHighlightEditorState] = useState(EditorState.createEmpty());
    const [categoryTree, setCategoryTree] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [imageWidthRatio, setImageWidthRatio] = useState(50);
    const [imageOption1, setImageOption1] = useState({ url: '', name: '', file: null });
    const [imageOption2, setImageOption2] = useState({ url: '', name: '', file: null });
    const [imgText, setImgText] = useState('');
    const isEditMode = initialData !== null;
    // Separate editor states
    const [editorStateOption1Top, setEditorStateOption1Top] = useState(() => EditorState.createEmpty());
    const [editorStateOption1Bottom, setEditorStateOption1Bottom] = useState(() => EditorState.createEmpty());
    const [editorStateOption2, setEditorStateOption2] = useState(() => EditorState.createEmpty());

    const [editorState, setEditorState] = useState(
        () => EditorState.createEmpty(),
    );
    const [allAuthors, setAllAuthors] = useState([]);

    const draftToHtmlConfig = {
        entityToHTML: (entity, originalText) => {
            if (entity.type === 'LINK') {
                return `<a href="${entity.data.url}" target="_blank" rel="noopener noreferrer">${originalText}</a>`;
            }
            return originalText;
        },
    };
    const [convertedContent, setConvertedContent] = useState(null); useEffect(() => {

        let html = convertToHTML(draftToHtmlConfig)(editorState.getCurrentContent());
        setConvertedContent(html);
    }, [editorState]);

    const handleExitClick = () => {

        const confirmClose = window.confirm(
            'Möchtest du wirklich schließen? Alle Änderungen gehen verloren.'
        );
        if (!confirmClose) return;

        onClose(true);
    };
    const today = new Date().toISOString().slice(0, 10);
    const [formData, setFormData] = useState({
        title: "",
        datum: today,
        autor: "",
        slug: "",
        isHighlighted: false,
        stil: ""
    });
    const parseBackendDate = (dateStr) => {
        if (!dateStr) return today; // fallback to today if undefined/null
        const [day, month, year] = dateStr.split('.');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    };

    useEffect(() => {
        if (initialData) {
            // Set main form data
            setFormData({
                title: initialData.title || "",
                datum: parseBackendDate(initialData.published_date),
                autor: initialData.author_name || "",
                slug: initialData.slug || "",
                isHighlighted: !!initialData.is_highlighted,
                stil: initialData.style || "",
            });
            if (initialData.author_name) {
                setAutorFromInitialData(true);
            }

            try {
                if (initialData.editor_state_option1_top) {
                    const rawContent = JSON.parse(initialData.editor_state_option1_top);
                    setEditorStateOption1Top(EditorState.createWithContent(convertFromRaw(rawContent)));
                } else {
                    setEditorStateOption1Top(EditorState.createEmpty());
                }

                if (initialData.highlight_editor_state) {
                    const rawContent = JSON.parse(initialData.highlight_editor_state);
                    setHighlightEditorState(EditorState.createWithContent(convertFromRaw(rawContent)));
                } else {
                    setHighlightEditorState(EditorState.createEmpty());
                }

                if (initialData.editor_state_option1_bottom) {
                    const rawContent = JSON.parse(initialData.editor_state_option1_bottom);
                    setEditorStateOption1Bottom(EditorState.createWithContent(convertFromRaw(rawContent)));
                } else {
                    setEditorStateOption1Bottom(EditorState.createEmpty());
                }

                if (initialData.editor_state_option2) {
                    const rawContent = JSON.parse(initialData.editor_state_option2);
                    setEditorStateOption2(EditorState.createWithContent(convertFromRaw(rawContent)));
                } else {
                    setEditorStateOption2(EditorState.createEmpty());
                }
            } catch (err) {
                console.error('Failed to parse editor state:', err);
                // fallback: clear editors
                setEditorStateOption1Top(EditorState.createEmpty());
                setHighlightEditorState(EditorState.createEmpty());
                setEditorStateOption1Bottom(EditorState.createEmpty());
                setEditorStateOption2(EditorState.createEmpty());
            }

            // Set selected categories if they exist
            if (initialData.categories) {
                setSelectedCategories(initialData.categories);
            }

            // Set imgText if available
            if (initialData.img_text) {
                setImgText(initialData.img_text);
            }

            if (initialData.selected_radio) {
                setSelectedRadio(initialData.selected_radio);
            }
            if (initialData.image) {
                if (initialData.selected_radio && initialData.selected_radio == 'option2') {
                    setImageOption2({
                        url: initialData.image,
                        name: getFilenameFromPath(initialData.image),
                        file: null,
                    });
                } else {
                    setImageOption1({
                        url: initialData.image,
                        name: getFilenameFromPath(initialData.image), // change if you have filename
                        file: null,
                    });
                }

            }
        }
    }, [initialData]);
    const getFilenameFromPath = (path) => {
        if (!path) return '';
        // Normalize both slashes to `/` to support Windows and Unix paths
        const normalizedPath = path.replace(/\\/g, '/');
        const parts = normalizedPath.split('/');
        return parts[parts.length - 1] || '';
    };




    useEffect(() => {
        setIsMounted(true);
    }, []);
    const handleChange = (e) => {
        const { name, value, type, multiple, options, checked } = e.target;
        if (type === 'select-multiple') {
            const selected = Array.from(options)
                .filter((opt) => opt.selected)
                .map((opt) => opt.value);

            setFormData((prev) => ({
                ...prev,
                [name]: selected,
            }));
        } else if (type === 'checkbox') {
            setFormData((prev) => ({
                ...prev,
                [name]: checked,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };
    useEffect(() => {
        const fetchCurrentUser = async () => {
            const token = localStorage.getItem('token');
            const res = await fetch('/api/auth/me', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return;
            const data = await res.json();
            setCurrentUser(data);
        };

        fetchCurrentUser();
    }, []);

    useEffect(() => {
        if (!currentUser || canSetAnyAuthor || autorFromInitialData) return;

        setFormData((prev) => ({
            ...prev,
            autor: `${currentUser.first_name} ${currentUser.family_name}`,
        }));
    }, [canSetAnyAuthor, currentUser, autorFromInitialData]);

    useEffect(() => {
        if (checkingAnyAuthorPermission || !canSetAnyAuthor || !currentUser) return;

        const fetchAuthors = async () => {
            const res = await fetch('/api/news/get_all_authors');
            if (!res.ok) return;
            const authors = await res.json();
            const fullName = `${currentUser.first_name} ${currentUser.family_name}`.trim();
            const alreadyInList = authors.some(
                (a) => a.author_name === fullName
            );

            if (!alreadyInList) {
                authors.unshift({
                    id: 'current-user', // optional placeholder
                    author_name: fullName,
                });
            }


            setAllAuthors(authors);
        };

        fetchAuthors();
    }, [checkingAnyAuthorPermission, canSetAnyAuthor, currentUser]);


    useEffect(() => {
        if (
            checkingAnyAuthorPermission ||
            !canSetAnyAuthor ||
            !currentUser ||
            !allAuthors.length ||
            autorFromInitialData // ✅ Prevent override!
        ) return;

        const fullName = `${currentUser.first_name} ${currentUser.family_name}`.trim();

        setFormData(prev => ({
            ...prev,
            autor: prev.autor?.trim() ? prev.autor : fullName
        }));
    }, [allAuthors, checkingAnyAuthorPermission, canSetAnyAuthor, currentUser, autorFromInitialData]);


    useEffect(() => {
        async function fetchCategories() {
            const res = await fetch('/api/categories/get_all_categories');
            const data = await res.json();
            // Save raw categories for tree build
            setCategories(data);

            // Build tree
            setCategoryTree(buildMuiTree(data));
        }
        fetchCategories();
    }, []);


    useEffect(() => {
        if (initialData == null) return;
        const namesFromBackend = initialData?.concat_categories
            .split(',')
            .map(name => name.trim());

        const matchedIds = categories
            .filter(cat => namesFromBackend.includes(cat.name))
            .map(cat => cat.name);

        setSelectedCategories(matchedIds);
    }, [initialData?.concat_categories, categories]);

    function resetAll() {
        setFormData({
            title: '',
            datum: '',
            autor: '',
            slug: '',
            isHighlighted: false,
        });
        setSelectedCategories([]);
        setEditorStateOption1Top(EditorState.createEmpty());
        setEditorStateOption1Bottom(EditorState.createEmpty());
        setEditorStateOption2(EditorState.createEmpty());
        setHighlightEditorState(EditorState.createEmpty());
        setSelectedRadio('option1');
        setImageOption1(null);
        setImageOption2(null);
        setImgText('');
        setImageWidthRatio(50);
    }

    function buildMuiTree(categories) {
        const map = {};
        const roots = [];

        categories.forEach(cat => {
            map[cat.name] = {
                id: cat.name,
                label: cat.name,
                children: [],
            };
        });

        categories.forEach(cat => {
            if (cat.parent_category && map[cat.parent_category]) {
                map[cat.parent_category].children.push(map[cat.name]);
            } else {
                roots.push(map[cat.name]);
            }
        });

        return roots;
    }

    const handleSubmit = async () => {
        // Upload image if file is selected
        let uploadedCustomImage = false;
        let imagePath = "";
        let fullHTML = "";
        let contextText = "";
        try {

            // Basic required fields validation
            if (!formData.title?.trim()) {
                showErrorToast('Ein Titel der News is verpflichtend');
                return;
            }
            if (!formData.datum) {
                showErrorToast('Ein Datum muss angegeben werden');
                return;
            }

            if (!formData.autor) {
                showErrorToast("Keine Autor:in ausgewählt!")
                return;
            }
            if (!formData.slug?.trim()) {
                showErrorToast("Ein Slug wird benötigt!")
                return;
            }

            // Validate categories
            if (!selectedCategories || selectedCategories.length === 0) {
                showErrorToast('Wähle mindestens eine Kategorie aus.')
                return;
            }

            // Validate editor content (top must not be empty)
            if (selectedRadio === 'option1' || selectedRadio === 'option3') {
                const topContent = editorStateOption1Top.getCurrentContent();
                if (!topContent.hasText()) {
                    showErrorToast((selectedRadio === 'option1') ? 'Der Textbereich (neben dem Bild) darf nicht leer sein.' : 'Der Textbereich darf nicht leer sein!')
                    return;
                }
            } else if (selectedRadio === 'option2') {
                const textContent = editorStateOption2.getCurrentContent();
                if (!textContent.hasText()) {
                    showErrorToast('Der Textbereich unterhalb des Bildes darf nicht leer sein.');
                    return;
                }
            }

            // check validation now transform data

            if ((selectedRadio === 'option1' && imageOption1?.file) || (selectedRadio === "option2" && imageOption2?.file)) {
                // A new image file has been selected → upload it
                const imageUploadFormData = new FormData();
                if (selectedRadio === 'option1' && imageOption1?.file) {
                    imageUploadFormData.append('image', imageOption1.file);
                } else if (selectedRadio === "option2" && imageOption2?.file) {
                    imageUploadFormData.append('image', imageOption2.file);
                }
                imageUploadFormData.append('slug', formData.slug);

                const res = await fetch('/api/upload/upload-image', {
                    method: 'POST',
                    body: imageUploadFormData,
                });

                if (!res.ok) throw new Error('Image upload failed');

                const dataImageUpload = await res.json();
                imagePath = dataImageUpload.path;
                uploadedCustomImage = true;
            } else {
                // No new image file selected → use existing one if available
                if (selectedRadio === 'option1' && imageOption1?.url) {
                    imagePath = imageOption1.url;
                } else if (selectedRadio === "option2" && imageOption2?.url) {
                    imagePath = imageOption2.url;
                } else {
                    // No existing image, fall back to default
                    imagePath = getDefaultImageForCategory(selectedCategories);
                }
            }
            const categoryString = selectedCategories.length > 0 ? selectedCategories.join(', ') : "Verein";
            if (selectedRadio === 'option1' || selectedRadio === 'option3') {
                const htmlTop = convertToHTML(draftToHtmlConfig)(editorStateOption1Top.getCurrentContent());
                const textTop = editorStateOption1Top.getCurrentContent().getPlainText();

                // Check if bottom block is enabled
                let htmlBottom = '';
                let textBottom = '';

                htmlBottom = convertToHTML(draftToHtmlConfig)(editorStateOption1Bottom.getCurrentContent());
                textBottom = editorStateOption1Bottom.getCurrentContent().getPlainText();

                contextText = `${textTop} ${textBottom}`
                if (selectedRadio === 'option1') {
                    fullHTML = generateOption1HTML({
                        image: imagePath, // replace after upload if needed
                        title: formData.title,
                        author_name: formData.autor,
                        category: categoryString,
                        date: formData.datum,
                        htmlTop,
                        htmlBottom,
                        imgText,
                        imageWidthRatio
                    });
                } else {
                    fullHTML = generateOption3HTML({
                        title: formData.title,
                        author_name: formData.autor,
                        category: categoryString,
                        date: formData.datum,
                        html: htmlTop,
                    });
                }

            } else if (selectedRadio === 'option2') {
                const html = convertToHTML(draftToHtmlConfig)(editorStateOption2.getCurrentContent());

                contextText = editorStateOption2.getCurrentContent().getPlainText();
                fullHTML = generateOption2HTML({
                    image: imagePath, // replace after upload if needed
                    title: formData.title,
                    author_name: formData.autor,
                    category: categoryString,
                    date: formData.datum,
                    html,
                    imgText
                });
            }

            const isHighlighted_html = convertToHTML(draftToHtmlConfig)(highlightEditorState.getCurrentContent());
            // Build payload object for backend



            const payload = {
                ...(isEditMode && { article_id: initialData.article_id }),
                title: formData.title,
                published_date: formData.datum,
                author_name: formData.autor?.trim() || '',
                categories: selectedCategories,   // array of strings
                slug: formData.slug,
                is_highlighted: formData.isHighlighted ? 1 : 0,
                image_path: imagePath,
                content_html: fullHTML,
                content_text: contextText,
                highlight_html: isHighlighted_html,
                img_text: imgText,
                image_width_ratio: imageWidthRatio,
                selected_radio: selectedRadio,

                highlight_editor_state: JSON.stringify(convertToRaw(highlightEditorState.getCurrentContent())),
                editor_state_option1_top: JSON.stringify(convertToRaw(editorStateOption1Top.getCurrentContent())),
                editor_state_option1_bottom: JSON.stringify(convertToRaw(editorStateOption1Bottom.getCurrentContent())),
                editor_state_option2: JSON.stringify(convertToRaw(editorStateOption2.getCurrentContent()))
            };
            // Send POST request as JSON
            const response = await fetch(isEditMode ? '/api/news/update_news' : '/api/news/create_news', {
                method: isEditMode ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errMsg = await response.text();
                if (response.status === 409) {
                    // Duplicate slug
                    showErrorToast('Fehler: Der Slug existiert bereits. Bitte wähle einen anderen.');
                    throw new Error('DUPLICATE_SLUG');
                }
                throw new Error(errMsg.message || 'Serverfehler');
            }

            const result = await response.json();
            resetAll()
            onClose(true);
            if (isEditMode) {
                showInfoToast(`"${formData.title}" wurde erfolgreich aktualisiert!`);
            } else {
                showInfoToast(`"${result.newsTitle}" wurde erfolgreich erstellt!`);
            }
        } catch (err) {
            console.error(err);
            showErrorToast('Fehler beim Speichern des Artikels: ' + err.message);
        }
    };


    if (checking) {
        return <div className={styles.container}><p>Lade...</p></div>;
    }

    const options = [
        { id: 'option1', label: 'Option 1', imgSrc: `${process.env.PUBLIC_URL}/assets/newsEditor/beispielbild-option1.png` },
        { id: 'option2', label: 'Option 2', imgSrc: `${process.env.PUBLIC_URL}/assets/newsEditor/beispielbild-option2.png` },
        { id: 'option3', label: 'Option 3', imgSrc: `${process.env.PUBLIC_URL}/assets/newsEditor/beispielbild-option3.png` },
    ];



    const renderContentLayout = () => {
        switch (selectedRadio) {
            case 'option1':
                return (
                    <>
                        <label className={styles.imageWidth}>
                            <div>Bildbreite (40%-60%):</div>
                            <input
                                type="number"
                                min="40"
                                max="60"
                                value={imageWidthRatio}
                                onChange={(e) => setImageWidthRatio(Number(e.target.value))}
                            />%
                        </label>
                        <StyleOneContent
                            image={imageOption1}
                            setImage={setImageOption1}
                            editorStateTop={editorStateOption1Top}
                            setEditorStateTop={setEditorStateOption1Top}
                            editorStateBottom={editorStateOption1Bottom}
                            setEditorStateBottom={setEditorStateOption1Bottom}
                            imageWidthRatio={imageWidthRatio}
                            setImgText={setImgText}
                            imgText={imgText}
                        />
                    </>
                );
            case 'option2':
                return (
                    <StyleTwoContent
                        image={imageOption2}
                        setImage={setImageOption2}
                        editorState={editorStateOption2}
                        setEditorState={setEditorStateOption2}
                        setImgText={setImgText}
                        imgText={imgText}
                    />
                );
            case 'option3':
                return (
                    <StyleThreeContent
                        editorState={editorStateOption1Top}
                        setEditorState={setEditorStateOption1Top}
                    />
                );

            default:
                return <p>Bitte einen Stil auswählen.</p>;
        }
    };

    const handlePreview = () => {
        let htmlTop = '';
        let htmlBottom = '';
        let html = '';
        let selectedImage = '';
        switch (selectedRadio) {
            case 'option1':
                htmlTop = convertToHTML(draftToHtmlConfig)(editorStateOption1Top.getCurrentContent());
                htmlBottom = convertToHTML(draftToHtmlConfig)(editorStateOption1Bottom.getCurrentContent());
                selectedImage = imageOption1?.url || '';
                break;
            case 'option2':
                html = convertToHTML(draftToHtmlConfig)(editorStateOption2.getCurrentContent());
                selectedImage = imageOption2?.url || '';
                break;
            case 'option3':
                html = convertToHTML(draftToHtmlConfig)(editorStateOption1Top.getCurrentContent());
                break;
            default:
                html = convertToHTML(draftToHtmlConfig)(editorState.getCurrentContent());
                selectedImage = '';
        }
        const categoryString = selectedCategories.length > 0 ? selectedCategories.join(', ') : "Verein";
        const previewData = {
            title: formData.title,
            author_name: formData.autor,
            date: formData.datum,
            category: categoryString,
            image: selectedRadio === 'option1' ? imageOption1.url : (imageOption2?.url || ''),
            htmlTop: DOMPurify.sanitize(htmlTop),
            html: DOMPurify.sanitize(html),
            imageWidthRatio,
            selectedLayout: selectedRadio,
            imgText: imgText
        };

        // Add htmlBottom only if the bottom editor is shown
        previewData.htmlBottom = DOMPurify.sanitize(htmlBottom);

        // Store in localStorage or pass via state/navigation
        localStorage.setItem('previewContent', JSON.stringify(previewData));
        window.open('/admin/panel/create-news/preview', '_blank');
    };



    return (
        <div className={styles.container}>
            <h1>
                {isEditMode ? 'News bearbeiten' : 'News erstellen'}
            </h1>
            <Icons.X className={styles.exit} onClick={() => { handleExitClick() }} />
            <form className={styles.formular} onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <div className={styles.section}>
                    <p className={styles.sectionTitle}>Allgemeine Informationen</p>
                    <div className={styles.area}>

                        <label className={styles.big}>
                            <div>News-Titel<span className={styles.required}>*</span></div>
                            <input
                                type="text"
                                placeholder="Ergebnisse Senioren"
                                name="title"
                                required
                                value={formData.title}
                                onChange={handleChange} />
                        </label>


                        <label>
                            <div>Datum<span className={styles.required}>*</span></div>
                            <input
                                type="date"
                                name="datum"
                                value={formData.datum} // controlled from formData
                                required
                                onChange={handleChange}
                            />
                        </label>


                        <label>
                            <div>Autor:in<span className={styles.required}>*</span></div>
                            {canSetAnyAuthor ? (
                                <>
                                    <input
                                        list="author-options"
                                        name="autor"
                                        required
                                        value={formData.autor}
                                        onChange={handleChange}
                                        placeholder="Autor eingeben oder wählen"
                                    />
                                    <datalist id="author-options">
                                        {allAuthors.map((user) => (
                                            <option
                                                key={user.author_name}
                                                value={user.author_name}
                                            />
                                        ))}
                                    </datalist>
                                </>
                            ) : (
                                <input
                                    type="text"
                                    name="autor"
                                    disabled
                                    required
                                    value={`${currentUser?.first_name ?? ''} ${currentUser?.family_name ?? ''}`}
                                />
                            )}
                        </label>
                    </div>
                    <div className={styles.area}>

                        <label className={styles.big}>
                            <div>Slug<span className={styles.required}>*</span></div>
                            <input
                                type="text"
                                placeholder="ergebnisse-senioren-20250413"
                                name="slug"
                                required
                                value={formData.slug}
                                onChange={handleChange} />
                        </label>


                        {canSetAnyAuthor && (
                            <label>
                                <div>Highlight<span className={styles.required}></span>
                                    <input
                                        type="checkbox"
                                        name="isHighlighted"
                                        checked={formData.isHighlighted || false}
                                        onChange={handleChange} />
                                </div>

                            </label>
                        )}


                    </div>
                    {formData.isHighlighted && (
                        <div className={styles.area}>
                            <label>
                                <div>Highlight Beschreibung<span className={styles.required}>*</span></div>
                                <Editor
                                    editorState={highlightEditorState}
                                    wrapperClassName={styles.editorWrapper}
                                    editorClassName={styles.editor}

                                    onEditorStateChange={setHighlightEditorState}
                                    toolbar={{
                                        options: [
                                            'inline',
                                            'blockType',
                                            'list',
                                            'textAlign',
                                            'link',
                                            'emoji',
                                            'history'
                                        ],
                                        blockType: {
                                            options: [
                                                'Normal', // unstyled text
                                                'H2',
                                                'H3',
                                            ],
                                        },
                                        inline: {

                                            options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace', 'superscript', 'subscript'],
                                        },
                                        list: {

                                            options: ['unordered', 'ordered', 'indent', 'outdent'],
                                        },
                                        textAlign: {

                                            options: ['left', 'center', 'right', 'justify'],
                                        },
                                        link: {
                                            inDropdown: false,
                                            // options: ['link', 'unlink'],
                                        },
                                        history: {
                                            options: ['undo', 'redo'],
                                        }
                                    }}
                                // hashtag={{
                                //     separator: ' ',
                                //     trigger: '#',
                                // }}
                                // mention={{
                                //     separator: ' ',
                                //     trigger: '@',
                                //     suggestions: [
                                //         { text: 'JavaScript', value: 'javascript', url: 'js' },
                                //         { text: 'Golang', value: 'golang', url: 'go' },
                                //     ],
                                // }}
                                />
                            </label>
                        </div>
                    )}
                    <div className={styles.area}>
                        <label>
                            <div>Wähle die Kategorie aus:<span className={styles.required}>*</span></div>
                            <p className={styles.small}>Versuche möglichst nur eine Kategorie auszuwählen. Wenn man <strong>STRG</strong> hält, können mehrere Kategorien ausgewählt werden.</p>
                            <div >
                                {categoryTree.length === 0 ? (
                                    <p>Lade Kategorien...</p>
                                ) : (
                                    <CategoryDropdown
                                        treeData={categoryTree}
                                        selected={selectedCategories}
                                        setSelected={setSelectedCategories}
                                        className={styles.dropdown}
                                    />
                                )}
                            </div>
                        </label>


                    </div>
                </div>
                <div className={styles.section}>
                    <p className={styles.sectionTitle}>News-Stil</p>
                    <div className={styles.area}>
                        <ImageRadioButtons
                            options={options}
                            name="stil"
                            selectedValue={selectedRadio}
                            onChange={setSelectedRadio}
                        />
                    </div>
                </div>
                <div className={styles.section}>
                    <p className={styles.sectionTitle}>News-Inhalt</p>

                    {renderContentLayout()}
                </div>

                <div className={styles.buttonGroup}>
                    <button className={styles.button} type="submit">
                        {isEditMode ? 'News aktualisieren' : 'News abschicken'}
                    </button>
                    <button
                        type="button"
                        className={styles.button}
                        onClick={() => handlePreview()}
                    >
                        Vorschau
                    </button>
                </div>
            </form >
            <button onClick={() => handleExitClick()}>Abbrechen</button>
        </div >
    );
}

export default NewsEditor;
